import React, { useEffect, useRef, useState } from "react";
import { Italic, Eyebrow } from "./_primitives";

const cities = [
  { name: "Delhi NCR",  focus: "Enterprise & SaaS",            x: 35, y: 22, tier: "Metro"  },
  { name: "Surat",      focus: "Textiles, trade & D2C",        x: 19, y: 47, tier: "Tier 2" },
  { name: "Nasik",      focus: "Agri-tech & wine valley",      x: 24, y: 53, tier: "Tier 2" },
  { name: "Indore",     focus: "Emerging startup ecosystem",   x: 29, y: 46, tier: "Tier 2" },
  { name: "Kalaburgi",  focus: "Pulses, agri & manufacturing", x: 31, y: 65, tier: "Tier 3" },
  { name: "Hyderabad",  focus: "Deep-tech & enterprise SaaS",  x: 36, y: 67, tier: "Metro"  },
  { name: "Visakhapatnam", focus: "Ports, energy & IT-SEZ",   x: 47, y: 64, tier: "Tier 2" },
  { name: "Vijayawada", focus: "Agri-fintech & trade",         x: 42, y: 70, tier: "Tier 2" },
  { name: "Hubbali",    focus: "Startup hub & deep tech",      x: 27, y: 70, tier: "Tier 2" },
  { name: "Mangaluru",  focus: "Co-working & deep tech",       x: 28, y: 75, tier: "Tier 2" },
  { name: "Coimbatore", focus: "Manufacturing & industrial tech", x: 35, y: 82, tier: "Tier 2" },
  { name: "Erode",      focus: "Textiles & home furnishings",  x: 37, y: 79, tier: "Tier 2" },
  { name: "Karaikudi",  focus: "Finance",                      x: 40, y: 86, tier: "Tier 3" },
];

export default function PanIndia() {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => entry.isIntersecting && setIsVisible(true),
      { threshold: 0.18 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || hovered !== null) return;
    const id = setInterval(() => setActive((i) => (i + 1) % cities.length), 2800);
    return () => clearInterval(id);
  }, [isVisible, hovered]);

  const hubIndex = cities.findIndex((c) => c.name === "Indore");
  const hub = cities[hubIndex];

  return (
    <section
      ref={ref}
      id="presence"
      data-testid="pan-india-section"
      className="relative bg-[#DCE8C9] overflow-hidden"
    >
      {/* Soft radial glow background */}
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(20,64,31,0.10), transparent 60%), radial-gradient(ellipse at 50% 100%, rgba(232,163,61,0.10), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div
          className="text-center mb-14 lg:mb-20"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.9s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        >
          <Eyebrow>Our Presence</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-[#0F2818]">
            We Go Where Founders <Italic>Actually</Italic> Work.
          </h2>
          <p className="mt-5 max-w-2xl mx-auto text-base lg:text-lg text-[#4A5A4F]">
            60% of India's next billion-dollar companies will come from Tier 2 & 3 cities.
            We'll be the first ones there.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-10 lg:gap-12">
          {/* Map column */}
          <div
            className="lg:col-span-7 relative order-2 lg:order-1"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(28px)",
              transition: "all 1.1s 0.15s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <div
              className="relative w-full mx-auto"
              style={{
                maxWidth: "min(640px, 90vw)",
                aspectRatio: "1 / 1",
              }}
            >
              {/* Halo */}
              <div
                className="absolute inset-[4%] pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, rgba(184,217,88,0.20), transparent 70%)",
                  filter: "blur(30px)",
                  opacity: isVisible ? 1 : 0,
                  transition: "opacity 1.4s 0.5s ease-out",
                }}
              />

              {/* India outline image */}
              <img
                src="/india-map.png"
                alt="Map of India highlighting SparkSeed presence"
                className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                style={{
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? "scale(1)" : "scale(0.96)",
                  transition:
                    "opacity 1.4s 0.2s cubic-bezier(0.22, 1, 0.36, 1), transform 1.6s 0.2s cubic-bezier(0.22, 1, 0.36, 1)",
                  mixBlendMode: "multiply",
                }}
                draggable={false}
              />

              {/* SVG: connection lines from Indore hub + travelling pulses */}
              <svg
                viewBox="0 0 100 100"
                className="absolute inset-0 w-full h-full pointer-events-none"
                preserveAspectRatio="none"
              >
                {cities.map((c, i) => {
                  if (i === hubIndex) return null;
                  const isActive = active === i || active === hubIndex;
                  const len = Math.hypot(c.x - hub.x, c.y - hub.y);
                  return (
                    <line
                      key={`line-${c.name}`}
                      x1={hub.x}
                      y1={hub.y}
                      x2={c.x}
                      y2={c.y}
                      stroke="#E8A33D"
                      strokeWidth={isActive ? 0.45 : 0.25}
                      strokeDasharray={`${len} ${len}`}
                      style={{
                        strokeDashoffset: isVisible ? 0 : len,
                        opacity: isVisible ? (isActive ? 1 : 0.5) : 0,
                        transition: `stroke-dashoffset 1.4s ${1.2 + i * 0.12}s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.5s ease-out, stroke-width 0.4s ease-out`,
                      }}
                    />
                  );
                })}

                {cities.map((c, i) => {
                  if (i === hubIndex || active !== i) return null;
                  return (
                    <circle key={`pulse-${c.name}`} r="0.7" fill="#E8A33D">
                      <animateMotion
                        dur="1.6s"
                        repeatCount="indefinite"
                        path={`M ${hub.x} ${hub.y} L ${c.x} ${c.y}`}
                      />
                    </circle>
                  );
                })}
              </svg>

              {/* City pins */}
              {cities.map((c, i) => {
                const isActive = active === i;
                return (
                  <button
                    key={c.name}
                    data-testid={`city-marker-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    onMouseEnter={() => {
                      setActive(i);
                      setHovered(i);
                    }}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => setActive(i)}
                    className="absolute -translate-x-1/2 -translate-y-1/2 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B8D958] rounded-full"
                    style={{ left: `${c.x}%`, top: `${c.y}%` }}
                    aria-label={c.name}
                  >
                    {isActive && (
                      <span className="absolute -inset-3 rounded-full bg-[#E8A33D]/35 animate-ping pointer-events-none" />
                    )}
                    <span
                      className={`relative block rounded-full border-2 border-[#F7F8EF] shadow-[0_4px_12px_rgba(20,64,31,0.45)] transition-all duration-500 ${
                        isActive
                          ? "w-4 h-4 bg-[#E8A33D] scale-110"
                          : "w-2.5 h-2.5 bg-[#14401F] group-hover:scale-150"
                      }`}
                    />
                    <span
                      className={`absolute left-5 top-1/2 -translate-y-1/2 whitespace-nowrap text-[11px] font-semibold tracking-wide pointer-events-none transition-all duration-300 px-2 py-0.5 rounded-md ${
                        isActive
                          ? "text-[#14401F] bg-[#F7F8EF]/95 opacity-100 translate-x-0 shadow-sm"
                          : "text-[#14401F] bg-[#F7F8EF]/75 opacity-80 group-hover:opacity-100"
                      }`}
                    >
                      {c.name}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="mt-6 text-center text-[10px] uppercase tracking-[0.3em] text-[#14401F]/55 font-medium">
              {cities.length} cities · 6 ecosystems · 1 mission
            </p>
          </div>

          {/* City detail card */}
          <div
            className="lg:col-span-5 order-1 lg:order-2"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(28px)",
              transition: "all 1.1s 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <div
              className="relative bg-[#F7F8EF] rounded-3xl shadow-[0_30px_60px_-30px_rgba(20,64,31,0.35)] border border-[#14401F]/10 p-8 lg:p-10"
              data-testid="city-detail-panel"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[#14401F]">
                  {cities[active].tier}
                </div>
                <div className="text-[10px] tracking-[0.3em] font-mono text-[#14401F]/40">
                  0{active + 1} / 0{cities.length}
                </div>
              </div>

              <h3
                key={cities[active].name}
                className="font-serif-display font-bold text-[#0F2818] mb-3"
                style={{
                  animation: "ss-fade-up 0.6s both",
                  fontSize: "clamp(1.85rem, 3.4vw, 3rem)",
                  lineHeight: 1.05,
                }}
              >
                {cities[active].name}
              </h3>
              <p
                key={`${cities[active].name}-focus`}
                className="text-base lg:text-lg text-[#4A5A4F] mb-8"
                style={{ animation: "ss-fade-up 0.6s 0.05s both" }}
              >
                {cities[active].focus}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-8">
                {cities.map((c, i) => (
                  <button
                    key={c.name}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => setActive(i)}
                    className={`text-left px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                      active === i
                        ? "bg-[#14401F] text-white shadow-md"
                        : "bg-[#14401F]/5 text-[#14401F] hover:bg-[#14401F]/15"
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>

              <div className="border-t border-[#14401F]/12 pt-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#14401F] font-bold mb-3">Why this matters</div>
                <ul className="space-y-2">
                  {[
                    "<8% of institutional capital reaches Tier 2/3 founders",
                    "On-the-ground operators in every market we enter",
                    "Local sourcing, local diligence, local mentorship",
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[#0F2818]/80">
                      <span className="mt-2 h-1 w-1 rounded-full bg-[#14401F] flex-shrink-0" />
                      <span dangerouslySetInnerHTML={{ __html: t }} />
                    </li>
                  ))}
                </ul>
              </div>

              {/* Gold dot */}
              <div className="absolute -top-3 -right-3 w-7 h-7 rounded-full bg-[#E8A33D] shadow-md" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
